import { useRef, useCallback } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useTranslation, useLang } from "@/lib/i18n";
import { useIsMobile } from "@/hooks/use-mobile";

const AdvisoryTeaserSection = () => {
  const prefersReduced = useReducedMotion();
  const isMobile = useIsMobile();
  const t = useTranslation();
  const { lang } = useLang();
  const at = t.aiTeams.advisoryTeaser;
  const advisoryHref = lang === "pl" ? "/pl/1-1" : "/1-1";
  const isFinePointer = typeof window !== "undefined" && window.matchMedia("(pointer: fine)").matches;

  const ctaRef = useRef<HTMLAnchorElement>(null);
  const ctaContainerRef = useRef<HTMLDivElement>(null);

  const anim = {
    initial: prefersReduced || isMobile ? { opacity: 0 } : { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" as const },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const },
  };

  const handleMove = useCallback((e: React.MouseEvent) => {
    if (prefersReduced || !isFinePointer || !ctaRef.current || !ctaContainerRef.current) return;
    const r = ctaContainerRef.current.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const dx = (e.clientX - cx) * 0.08;
    const dy = (e.clientY - cy) * 0.08;
    const max = 8;
    ctaRef.current.style.transform = `translate(${Math.max(-max, Math.min(max, dx))}px, ${Math.max(-max, Math.min(max, dy))}px)`;
  }, [prefersReduced, isFinePointer]);

  const handleLeave = useCallback(() => {
    if (ctaRef.current) ctaRef.current.style.transform = "translate(0,0)";
  }, []);

  return (
    <section className="py-16">
      <div className="container mx-auto px-6">
        <motion.div
          initial={anim.initial}
          whileInView={anim.whileInView}
          viewport={anim.viewport}
          transition={anim.transition}
          className="max-w-xl mx-auto rounded-xl border border-border bg-card/60 backdrop-blur p-6 relative overflow-hidden"
          ref={ctaContainerRef}
          onMouseMove={handleMove}
          onMouseLeave={handleLeave}
        >
          {/* Conic gradient mesh */}
          <div
            className="absolute inset-0 pointer-events-none opacity-40"
            style={!prefersReduced ? {
              background: "conic-gradient(from 0deg, hsl(var(--primary) / 0.08), hsl(var(--accent) / 0.08), transparent)",
              animation: "spin 40s linear infinite",
            } : {
              background: "conic-gradient(from 0deg, hsl(var(--primary) / 0.08), hsl(var(--accent) / 0.08), transparent)",
            }}
          />

          <div className="relative z-10">
            <p className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-primary mb-3">
              {at.eyebrow}
            </p>
            <h3 className="text-2xl font-display mb-3 leading-tight">
              {at.heading}
            </h3>
            <p className="text-muted-foreground font-body text-sm leading-relaxed mb-5">
              {at.body}
            </p>
            <a
              ref={ctaRef}
              href={advisoryHref}
              className="text-sm font-body font-medium text-primary inline-flex items-center gap-1 group/link"
              style={{ willChange: "transform" }}
            >
              {at.ctaLabel}
              <ArrowRight size={14} className="group-hover/link:translate-x-1 transition-transform" />
            </a>
            <p className="text-xs font-body text-muted-foreground mt-4">
              <span className="text-gradient font-semibold">
                {at.meta.split('/')[0].trim()}
              </span>
              {at.meta.includes('/') && ` / ${at.meta.split('/').slice(1).join('/').trim()}`}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AdvisoryTeaserSection;
